/**
 * Halloween campaign — GA4 measurement (WEI-29, plan signed off 2026-09-23).
 *
 * Sends the campaign's own events to the store's GA4 property. The Shopify Google & YouTube
 * app already reports page_view / add_to_cart / begin_checkout / purchase from its sandboxed
 * pixel, so none of those are sent here; this file adds only what that pixel cannot see:
 *
 *   section_view       a band >= half on screen for 1s          section_name
 *   view_item_list     treats / shop-window cards seen for 1s   item_list_name + items
 *   select_item        a card's link                            item_list_name + items
 *   add_to_cart_click  a card's add button (intent)             item_list_name + items
 *   add_to_cart_success  that add's /cart/add returned ok          item_list_name + items
 *   add_to_cart_fail   that add's /cart/add failed                item_list_name + items + status / reason
 *   tab_click          a treats tab that was not selected       tab_name
 *   cta_click          View All / Change / Shop Now             cta_name (+ tab_name)
 *   checkout_handoff   cart checkout / PDP Buy Now click         cta_name
 *   reward_unlocked    cart subtotal first crosses a tier       reward_tier
 *   splash_end         the opening splash played out or skipped outcome
 *
 * One visitor, one identity: the app pixel runs in the page itself and installs gtag there
 * (window.dataLayer carries its `config G-BQPRTYRWS5` and page_view). Nothing here loads or
 * configures a second copy; events are handed to that same gtag once it has configured the
 * property, so they land on the same client and session as the app's own page_view and
 * add_to_cart — which is what lets one GA funnel span both. A visitor the app never tags
 * (consent, blocker) is never tagged here either.
 *
 * Order is part of the data (WEI-29 audit, 2026-09-24). A GA funnel step only counts when its
 * event is timestamped after the previous step's, and the timestamp is the moment the request
 * reaches GA. So events leave one per request, in order, SEND_GAP_MS apart, starting a beat
 * after the app's config (never alongside its page_view); and reward_unlocked waits
 * REWARD_DELAY_MS, because Shopify reports the app's add_to_cart 2-3s after the cart changes
 * and an unlock sent at once would land before the add that caused it.
 *
 * Item ids copy the app pixel's `shopify_ZZ_<product>_<variant>` exactly (read off the
 * property's ecommerce report). GA does not carry a list's name onto the app's add_to_cart
 * or purchase, so per-list reporting stops at impressions and clicks.
 *
 * Loaded site-wide but idle off the campaign page unless this visit has been to it (tracked in
 * localStorage for GA's 30-minute session, so a product opened in a new tab still counts): a
 * tier crossed on a product page after arriving from the campaign is still reported.
 * Append ?hw_ga_debug=1 once to route this tab's events to GA DebugView.
 */
(() => {
 const MEASUREMENT_ID = 'G-BQPRTYRWS5';
 const CAMPAIGN = '.halloween-campaign';
 const LANDING_VALUE = 'halloween_sale';
 const DWELL_MS = 1000;
 const TAG_POLL_MS = 500;
 const TAG_WAIT_MS = 30000;
 const TAG_SETTLE_MS = 1000;
 const SEND_GAP_MS = 300;
 const REWARD_DELAY_MS = 5000;
 const VISIT_MS = 30 * 60 * 1000;
 const THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20);

 const script = document.currentScript;

 // Property access sits inside the try as well: reading window.localStorage itself throws
 // when site data is blocked. A storage that cannot be used simply remembers nothing.
 const storageOf = (area) => ({
  get: (key) => {
   try {
    return window[area].getItem(key);
   } catch (error) {
    return null;
   }
  },
  set: (key, value) => {
   try {
    window[area].setItem(key, value);
   } catch (error) {
    // Private mode or storage disabled.
   }
  },
  remove: (key) => {
   try {
    window[area].removeItem(key);
   } catch (error) {
    // Private mode or storage disabled.
   }
  }
 });
 const tabStore = storageOf('sessionStorage');
 const visitStore = storageOf('localStorage');

 const campaign = document.querySelector(CAMPAIGN);
 const inVisit = Date.now() - (Number(visitStore.get('hw_lp_at')) || 0) < VISIT_MS;
 if (!campaign && !inVisit) return;
 visitStore.set('hw_lp_at', String(Date.now()));
 // A new visit starts its tier baseline over from the cart as it stands.
 if (!inVisit) visitStore.remove('hw_tiers');

 if (/[?&]hw_ga_debug=1\b/.test(window.location.search)) tabStore.set('hw_ga_debug', '1');
 const debug = tabStore.get('hw_ga_debug') === '1';
 const currency = window.Shopify?.currency?.active || 'USD';

 // ------------------------------------------------------------------ transport

 // The app's gtag has claimed the property once its own `config` sits in the dataLayer.
 const tagReady = () =>
  Array.isArray(window.dataLayer) &&
  window.dataLayer.some((command) => command && command[0] === 'config' && command[1] === MEASUREMENT_ID);

 // gtag.js only accepts the Arguments object itself, which an arrow function does not have.
 // Pushing one straight onto the dataLayer is what window.gtag does, without claiming that
 // global from the app.
 // eslint-disable-next-line func-names
 const gtag = function () {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
 };

 const outbox = [];
 let transport = 'idle'; // idle -> waiting -> ready | gone
 let pacing = false;

 const emit = ([name, params]) =>
  gtag('event', name, { ...params, send_to: MEASUREMENT_ID, ...(debug ? { debug_mode: true } : {}) });

 // One event per request, SEND_GAP_MS apart: events handed over in the same instant reach GA
 // with the same timestamp, and a funnel then orders them arbitrarily (measured 2026-09-24).
 const pump = () => {
  if (transport !== 'ready' || pacing || !outbox.length) return;
  emit(outbox.shift());
  pacing = true;
  setTimeout(() => {
   pacing = false;
   pump();
  }, SEND_GAP_MS);
 };

 // Leaving the page cannot wait for the pacing: whatever is still queued goes now.
 const flushNow = () => {
  if (transport === 'gone' || !tagReady()) return;
  transport = 'ready';
  outbox.splice(0).forEach(emit);
 };

 const waitForTag = () => {
  transport = 'waiting';
  const started = Date.now();
  const poll = () => {
   if (tagReady()) {
    // A beat after the app's config, so the first of these never lands with its page_view.
    setTimeout(() => {
     transport = 'ready';
     pump();
    }, TAG_SETTLE_MS);
    return undefined;
   }
   if (Date.now() - started > TAG_WAIT_MS) {
    transport = 'gone';
    outbox.length = 0;
    return undefined;
   }
   return setTimeout(poll, TAG_POLL_MS);
  };
  poll();
 };

 const send = (name, params) => {
  if (transport === 'gone') return;
  outbox.push([name, params]);
  if (transport === 'idle') waitForTag();
  pump();
 };

 // ------------------------------------------------------------------ add-to-cart result
 // add_to_cart_click is intent only. The /cart/add(.js) request that follows it reports the
 // outcome: add_to_cart_success when it returns ok, add_to_cart_fail (status / reason, e.g.
 // Shopify's "sold out" description) when it does not. Only a request within 15s of a card's
 // add click counts, so other cart writes on the page never produce these events.

 let pendingAtc = null;
 const atcResult = (ok, status, reason) => {
  const pending = pendingAtc;
  if (!pending || Date.now() - pending.at > 15000) return;
  pendingAtc = null;
  send(
   ok ? 'add_to_cart_success' : 'add_to_cart_fail',
   ok
    ? { ...pending.params }
    : {
       ...pending.params,
       status: status || 0,
       reason: String(reason || (status ? `http_${status}` : 'network_error')).slice(0, 100)
      }
  );
 };
 if (typeof window.fetch === 'function') {
  const origFetch = window.fetch;
  window.fetch = function (input) {
   const result = origFetch.apply(this, arguments);
   const url = typeof input === 'string' ? input : input?.url || '';
   if (/\/cart\/add(\.js)?(\?|#|$)/.test(String(url))) {
    result.then(
     (response) => {
      if (response.ok) return atcResult(true, response.status);
      response
       .clone()
       .json()
       .then((data) => atcResult(false, response.status, data?.description || data?.message))
       .catch(() => atcResult(false, response.status));
     },
     () => atcResult(false, 0, 'network_error')
    );
   }
   return result;
  };
 }

 // ------------------------------------------------------------------ reward tiers

 const giftConfig = (() => {
  try {
   return JSON.parse(document.getElementById('cart-free-gift-data')?.textContent || 'null');
  } catch (error) {
   return null;
  }
 })();

 const giftProperty = giftConfig?.property || '_free_gift';
 const freeShippingCents = Math.round(parseFloat(script?.dataset.freeShipping || '') * 100) || 0;
 const tiers = [freeShippingCents, ...(Array.isArray(giftConfig?.tiers) ? giftConfig.tiers : [])]
  .filter((cents) => cents > 0)
  .map((cents) => ({ name: String(cents / 100), cents }));

 const isGift = (line) => line.gift === true || Boolean(line.properties?.[giftProperty]);
 const subtotalOf = (cart) =>
  (Array.isArray(cart?.items) ? cart.items : [])
   .filter((line) => !isGift(line))
   .reduce((sum, line) => sum + (line.final_line_price || 0), 0);
 const tiersMet = (cents) => tiers.filter((tier) => cents >= tier.cents).map((tier) => tier.name);

 const readReached = () => {
  try {
   const saved = JSON.parse(visitStore.get('hw_tiers') || 'null');
   return Array.isArray(saved) ? saved : null;
  } catch (error) {
   return null;
  }
 };

 // The first page of the visit takes the cart as it rendered as the baseline, silently:
 // a tier the cart already sat above before the visit is not something this visit unlocked.
 const reached = new Set(readReached() || (giftConfig?.cart ? tiersMet(subtotalOf(giftConfig.cart)) : []));
 visitStore.set('hw_tiers', JSON.stringify([...reached]));

 const pendingRewards = [];
 let rewardTimer = 0;
 const releaseRewards = () => {
  clearTimeout(rewardTimer);
  rewardTimer = 0;
  pendingRewards.splice(0).forEach((params) => send('reward_unlocked', params));
 };

 const onCart = (cart) => {
  if (!cart) return;
  const cents = subtotalOf(cart);
  const fresh = tiersMet(cents).filter((name) => !reached.has(name));
  if (!fresh.length) return;
  fresh.forEach((name) => {
   reached.add(name);
   pendingRewards.push({ reward_tier: name, value: cents / 100, currency });
  });
  visitStore.set('hw_tiers', JSON.stringify([...reached]));
  // Every crossing restarts the wait, so the unlocks always trail the latest add.
  clearTimeout(rewardTimer);
  rewardTimer = setTimeout(releaseRewards, REWARD_DELAY_MS);
 };

 document.addEventListener('cart:change', (event) => onCart(event.detail?.cart));
 document.addEventListener('cart:reconciled', (event) => onCart(event.detail?.cart));

 // Anything the pacing or the reward delay still holds goes out before the page does.
 const beforeLeave = [];
 const leave = () => {
  beforeLeave.forEach((flush) => flush());
  releaseRewards();
  flushNow();
 };
 window.addEventListener('pagehide', leave);
 document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && leave());

 // Shopify's checkout pixel can miss begin_checkout after an in-app browser leaves the
 // storefront. Record the handoff while this page's GA identity is still available.
 // Keep this distinct from begin_checkout so a normal checkout is never counted twice.
 let checkoutReported = false;
 const reportCheckout = (trigger) => {
  if (checkoutReported || trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;
  checkoutReported = true;
  send('checkout_handoff', { cta_name: trigger.matches('[data-pdp-buynow]') ? 'pdp_buy_now' : 'cart_checkout' });
  flushNow();
 };

 document.addEventListener(
  'click',
  (event) => {
   const target = event.target instanceof Element ? event.target : null;
   const trigger = target?.closest('button[name="checkout"], [data-pdp-buynow], a[href*="/checkout"], a[href*="?checkout"]');
   if (trigger) reportCheckout(trigger);
  },
  true
 );
 document.addEventListener('submit', (event) => event.submitter?.name === 'checkout' && reportCheckout(event.submitter), true);

 if (!campaign) return;

 // ------------------------------------------------------------------ campaign order tag

 // `_landing` rides the cart into the order's note attributes (the leading underscore keeps
 // it out of checkout), which is how Shopify-side revenue and AOV isolate campaign orders.
 // Written once per cart: a new cart (after a purchase) is tagged again on the next visit.
 const cartToken = () => (document.cookie.match(/(?:^|; )cart=([^;]*)/) || [])[1] || '';

 const tagCart = () => {
  const token = cartToken();
  if (token && visitStore.get('hw_landing_cart') === token) return;
  const root = window.Shopify?.routes?.root || '/';
  const write = () =>
   fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { _landing: LANDING_VALUE } })
   }).then((response) => response.ok && visitStore.set('hw_landing_cart', cartToken()));
  const lane = window.NexbieCart?.enqueueMutation;
  typeof lane === 'function' ? lane(write) : write().catch(() => {});
 };

 // ------------------------------------------------------------------ items

 const listOf = (el) => el.closest('[data-hw-list]')?.dataset.hwList || '';

 const itemOf = (el) => ({
  item_id: `shopify_ZZ_${el.dataset.hwItemProduct}_${el.dataset.hwItemVariant}`,
  item_name: el.dataset.hwItemName,
  ...(el.dataset.hwItemType ? { item_category: el.dataset.hwItemType } : {}),
  price: Number(el.dataset.hwItemPrice) / 100,
  index: Number(el.dataset.hwItemIndex) || undefined,
  item_list_id: listOf(el),
  item_list_name: listOf(el),
  quantity: 1
 });

 const itemPayload = (el) => ({
  item_list_id: listOf(el),
  item_list_name: listOf(el),
  currency,
  items: [itemOf(el)]
 });

 const pendingLists = new Map();
 let flushTimer = 0;

 const flushImpressions = () => {
  clearTimeout(flushTimer);
  flushTimer = 0;
  pendingLists.forEach((items, list) => send('view_item_list', { item_list_id: list, item_list_name: list, currency, items }));
  pendingLists.clear();
 };

 const queueImpression = (el) => {
  const list = listOf(el);
  pendingLists.set(list, [...(pendingLists.get(list) || []), itemOf(el)]);
  if (!flushTimer) flushTimer = setTimeout(flushImpressions, DWELL_MS);
 };

 beforeLeave.push(flushImpressions);

 // ------------------------------------------------------------------ exposure

 const splashUp = () => document.querySelector('[data-hw-splash]')?.open === true;
 // The shop window's second set stays laid out under `visibility: hidden` until Change is
 // pressed, and a hidden tab panel is `hidden`: neither is on screen however it intersects.
 const rendered = (el) => el.isConnected && !el.closest('[hidden]') && getComputedStyle(el).visibility === 'visible';
 const onScreen = (entry) =>
  entry.isIntersecting &&
  (entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= (entry.rootBounds?.height || window.innerHeight) * 0.5);

 const watched = new Map();
 const seenKeys = new Set();

 const arm = (el) => {
  const watch = watched.get(el);
  if (!watch || watch.timer || !watch.visible || seenKeys.has(watch.key)) return;
  watch.timer = setTimeout(() => {
   watch.timer = 0;
   if (!watch.visible || seenKeys.has(watch.key) || splashUp() || !rendered(el)) return;
   seenKeys.add(watch.key);
   watch.onSeen();
  }, DWELL_MS);
 };

 const rearm = () => watched.forEach((_, el) => arm(el));

 const observer =
  typeof IntersectionObserver === 'undefined'
   ? null
   : new IntersectionObserver(
     (entries) =>
      entries.forEach((entry) => {
       const watch = watched.get(entry.target);
       if (!watch) return;
       watch.visible = onScreen(entry);
       if (watch.visible) {
        arm(entry.target);
       } else {
        clearTimeout(watch.timer);
        watch.timer = 0;
       }
      }),
     { threshold: THRESHOLDS }
    );

 const watch = (el, key, onSeen) => {
  if (!observer) return;
  watched.set(el, { key, onSeen, visible: false, timer: 0 });
  observer.observe(el);
 };

 campaign.querySelectorAll('[data-hw-track-section]').forEach((el) => {
  const name = el.dataset.hwTrackSection;
  watch(el, `section:${name}`, () => send('section_view', { section_name: name }));
 });

 // Keyed by list and item, so the shop window's second conveyor run (the same eight cases
 // again, for the seamless loop) never reports a case twice.
 campaign.querySelectorAll('[data-hw-item]').forEach((el) => {
  watch(el, `item:${listOf(el)}:${el.dataset.hwItemProduct}`, () => queueImpression(el));
 });

 // Change flips the window's set behind the dark: cases already in view become visible
 // without any intersection change, so they are re-checked on the flip itself.
 const windowFrame = campaign.querySelector('.hw-window__frame');
 if (windowFrame && typeof MutationObserver !== 'undefined') {
  new MutationObserver(rearm).observe(windowFrame, { attributes: true, attributeFilter: ['data-hw-set'] });
 }

 const onSplashEnd = (outcome) => {
  if (outcome === 'complete' || outcome === 'skip') send('splash_end', { outcome });
  // The layer fades for up to 400ms before it is removed; count nothing under it.
  setTimeout(rearm, 450);
 };
 // This file is deferred and the splash is not: a quick skip (or the snippet's inline guard)
 // can end the show before we get here, so the outcome is also left on <html>.
 const splashEnded = document.documentElement.dataset.hwSplashEnd;
 splashEnded
  ? onSplashEnd(splashEnded)
  : document.addEventListener('halloween:splash-end', (event) => onSplashEnd(event.detail?.outcome), { once: true });

 // ------------------------------------------------------------------ clicks

 // Capture phase: the campaign script stops propagation on the add buttons, and the tab has
 // to be read before its own handler marks it selected.
 document.addEventListener(
  'click',
  (event) => {
   const target = event.target instanceof Element ? event.target : null;
   if (!target || !target.closest(CAMPAIGN)) return;

   const tab = target.closest('[data-hw-tab]');
   if (tab && tab.getAttribute('aria-selected') !== 'true') send('tab_click', { tab_name: tab.dataset.hwTabName });

   const cta = target.closest('[data-hw-track-cta]');
   if (cta) {
    const tabName = cta.closest('[data-hw-tab-name]')?.dataset.hwTabName;
    send('cta_click', { cta_name: cta.dataset.hwTrackCta, ...(tabName ? { tab_name: tabName } : {}) });
   }

   const item = target.closest('[data-hw-item]');
   if (!item) return;
   if (target.closest('[data-hw-add]')) {
    const params = itemPayload(item);
    pendingAtc = { at: Date.now(), params };
    send('add_to_cart_click', { ...params });
   } else if (target.closest('a[href]')) {
    send('select_item', itemPayload(item));
   }
  },
  true
 );

 // ------------------------------------------------------------------ boot

 const idle = window.requestIdleCallback || ((task) => setTimeout(task, 2000));
 window.addEventListener('load', () => idle(tagCart), { once: true });
})();
